import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Typography,
    Paper,
    Checkbox,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';
import { UploadFileOutlinedIcon, FileDownloadOutlinedIcon, CheckCircleOutlineIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import { CSVFileIcon } from 'icons/fileTypeIcons';
import TeamMemberRequiredFields from './TeamMemberRequiredFields';
import TeamContactLinkItem from 'team/components/editMember/links/TeamContactLinkItem';
// TeamContactLinkConfig removed - using individual plan components
import { getCSVImportContactLinkTypes } from '../../constants/teamContactLinkTypes';
import { useCreateTeamMember } from 'hooks/useCreateTeamMember';
import { useNavigate } from 'react-router-dom';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import FileImportUtils from 'utils/contacts/FileImportUtils';

const TeamImportCsvContent = ({ onDataChange, onValidationChange }) => {
    const navigate = useNavigate();
    const { createMultipleTeamMembers, loading: isCreatingMembers } = useCreateTeamMember();
    const [file, setFile] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationResults, setValidationResults] = useState({ valid: 0, invalid: 0 });
    const [currentStep, setCurrentStep] = useState(1); // 1 = Upload, 2 = Mapping
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const [mappings, setMappings] = useState({});
    const [checkedFields, setCheckedFields] = useState({
        email: true, // Email checked by default (required)
        name: false,
        jobtitle: false,
        company: false,
        location: false,
        bio: false,
        photo: false,
        email2: false,
        phonenumber: false,
        website: false,
        linkedin: false
    });
    
    // Snackbar state for notifications
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Contact link configuration state
    const [contactLinkConfig, setContactLinkConfig] = useState({
        email2: null,
        phonenumber: null,
        website: null,
        linkedin: null
    });
    const [contactLinkModal, setContactLinkModal] = useState({
        open: false,
        field: null,
        contactLink: null
    });

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Get available columns for mapping (excludes already used columns and internal fields)
    const getAvailableColumns = (field) => {
        if (!csvData || csvData.length === 0) return [];
        const columns = Object.keys(csvData[0]);
        const usedColumns = Object.values(mappings).filter(column => column && column !== mappings[field]);
        // Filter out internal validation fields
        const internalFields = ['id', 'isValid', 'errors'];
        return columns.filter(column =>
            !usedColumns.includes(column) &&
            !internalFields.includes(column)
        );
    };

    // Handle mapping changes
    const handleMappingChange = (field, column) => {
        setMappings(prev => ({
            ...prev,
            [field]: column
        }));
    };

    // Handle checkbox changes
    const handleCheckboxChange = (field) => {
        // Don't allow unchecking email (required)
        if (field === 'email') return;

        setCheckedFields(prev => ({
            ...prev,
            [field]: !prev[field]
        }));

        // If unchecking, clear the mapping and contact link config
        if (checkedFields[field]) {
            setMappings(prev => ({
                ...prev,
                [field]: ''
            }));
            setContactLinkConfig(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Handle contact link configuration
    const handleContactLinkClick = (field) => {
        setContactLinkModal({
            open: true,
            field: field,
            contactLink: contactLinkConfig[field]
        });
    };

    const handleContactLinkSave = (contactLinkData) => {
        setContactLinkConfig(prev => ({
            ...prev,
            [contactLinkModal.field]: contactLinkData
        }));
        setContactLinkModal({
            open: false,
            field: null,
            contactLink: null
        });
    };

    const handleContactLinkModalClose = () => {
        setContactLinkModal({
            open: false,
            field: null,
            contactLink: null
        });
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragActive) setDragActive(true);
    };

    // Removed isValidImportFile - now using FileImportUtils.validateFileType() which handles all file types

    const processFile = async (file) => {
        if (!file) return;

        // Use FileImportUtils for validation (same as leads import)
        if (!FileImportUtils.validateFileType(file)) {
            setError(`File "${file.name}" is not a supported file type or exceeds size limit. Please upload CSV or Excel files.`);
            return;
        }

        // Call handleFileSelect directly with the file (same as leads import)
        await handleFileSelect(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            await processFile(files[0]);
        }
    };

    const handleFileInput = async (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            await processFile(files[0]);
        }
    };

    // Removed parseCsvFile - now using FileImportUtils.importFile() which handles both CSV and Excel files

    const handleFileSelect = async (selectedFile) => {
        if (!selectedFile) return;

        setFile(selectedFile);
        setError('');
        setLoading(true);

        try {
            // Use FileImportUtils.importFile() to handle both CSV and Excel files (same as leads import)
            const { data: importedData, columns } = await FileImportUtils.importFile(selectedFile);

            // Check if required headers exist (case-insensitive)
            const requiredHeaders = ['email'];
            const missingHeaders = requiredHeaders.filter(h =>
                !columns.some(header => header.toLowerCase() === h.toLowerCase())
            );

            if (missingHeaders.length > 0) {
                throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
            }

            // Filter out empty rows to ensure clean data
            const validRows = importedData.filter(row => {
                return Object.values(row).some(val =>
                    val !== null && val !== undefined && val !== '');
            });

            // Process data and validate emails
            const data = validRows.map((row, index) => {
                // Find email field (case-insensitive) for validation
                const emailField = columns.find(h => h.toLowerCase() === 'email');
                const emailValue = emailField ? (row[emailField] || '') : '';

                return {
                    ...row,
                    id: index,
                    isValid: validateEmail(emailValue)
                };
            });

            setCsvData(data);

            const valid = data.filter(row => row.isValid).length;
            const invalid = data.length - valid;
            setValidationResults({ valid, invalid });

            if (invalid > 0) {
                setError(`${invalid} rows have validation errors. Please review the data below.`);
            }

            // Notify parent
            const validData = data.filter(row => row.isValid);
            onDataChange(validData);
            onValidationChange(validData.length > 0);

            // Auto-map email column if it exists (case-insensitive)
            if (data.length > 0) {
                const emailColumn = columns.find(col => col.toLowerCase() === 'email');
                if (emailColumn && !mappings.email) {
                    setMappings(prev => ({
                        ...prev,
                        email: emailColumn
                    }));
                }
                setCurrentStep(2);
            }
        } catch (err) {
            setError(err.message);
            setCsvData([]);
            onDataChange([]);
            onValidationChange(false);
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = () => {
        const csvContent = "email,name,jobtitle,company,location,bio,photo,phonenumber,website,linkedin,instagram\nexample@company.com,John Doe,Manager,Acme Inc,New York,Software developer with 5 years experience,profile.jpg,+1234567890,https://johndoe.com,https://linkedin.com/in/johndoe,@johndoe\n";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'team_members_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleImportMembers = async () => {
        // Validate that email mapping is set
        if (!mappings.email) {
            setError('Please map the email column before importing.');
            setSnackbarMessage('Please map the email column before importing.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Extract emails from CSV data using the email mapping
        const emails = csvData
            .map(row => {
                const emailValue = row[mappings.email];
                return emailValue ? emailValue.trim().toLowerCase() : null;
            })
            .filter(email => email && validateEmail(email));

        if (emails.length === 0) {
            setError('No valid emails found in the mapped column.');
            setSnackbarMessage('No valid emails found in the mapped column.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        // Remove duplicates
        const uniqueEmails = [...new Set(emails)];

        try {
            console.log('🔵 Starting CSV import for emails:', uniqueEmails);
            const result = await createMultipleTeamMembers(uniqueEmails, true);
            console.log('🔵 Import result:', result);

            // Show success message
            if (result.successCount > 0) {
                let message;
                if (result.failedCount > 0) {
                    const failedDetails = result.failed.map(f => `${f.email}: ${f.error}`).join('; ');
                    message = `Successfully imported ${result.successCount} member(s). ${result.failedCount} failed: ${failedDetails}`;
                    setSnackbarSeverity('warning');
                } else {
                    message = `Successfully imported ${result.successCount} member(s)!`;
                    setSnackbarSeverity('success');
                }

                setSnackbarMessage(message);
                setSnackbarOpen(true);

                // Clear the form
                setCsvData([]);
                setFile(null);
                setMappings({});
                setCurrentStep(1);
                onDataChange([]);
                onValidationChange(false);

                // Navigate after short delay to show message
                setTimeout(() => {
                    navigate('/team/members');
                }, 1500);
            } else {
                // All imports failed
                const failedDetails = result.failed.map(f => `${f.email}: ${f.error}`).join('; ');
                const errorMessage = failedDetails || 'Failed to import team members. Please try again.';
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (err) {
            console.error('🔴 Import error:', err);
            setSnackbarMessage(err.message || 'Failed to import team members');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    return (
        <Box sx={{
            padding: '2rem',
            paddingTop: '3rem',
            maxWidth: '700px' // Reduce content width
        }}>
            {/* Header with Title and Step Buttons */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                width: '100%'
            }}>
                {/* Title */}
                <Typography variant="h5" sx={{
                    fontSize: '1.5rem',
                    fontWeight: 500,
                    color: '#4f4f4f'
                }}>
                    Import members
                </Typography>

                {/* Step Buttons */}
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                    <Box
                        onClick={() => setCurrentStep(1)}
                        sx={{
                            backgroundColor: currentStep === 1 ? '#FFFFFF' : '#F7F7F7',
                            color: currentStep === 1 ? '#4F4F4F' : '#888888',
                            border: currentStep === 1 ? '1px solid #E0E0E0' : 'none',
                            padding: '0 24px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '100px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        1 - Upload
                    </Box>
                    <Box
                        onClick={() => csvData.length > 0 && setCurrentStep(2)}
                        sx={{
                            backgroundColor: currentStep === 2 ? '#FFFFFF' : '#F7F7F7',
                            color: currentStep === 2 ? '#4F4F4F' : '#888888',
                            border: currentStep === 2 ? '1px solid #E0E0E0' : 'none',
                            padding: '0 24px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '100px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: csvData.length > 0 ? 'pointer' : 'default',
                            transition: 'all 0.2s'
                        }}
                    >
                        2 - Mapping
                    </Box>
                </Box>
            </Box>

            {/* Content based on current step */}
            {currentStep === 1 && (
                <>
                    {/* File Upload Drag & Drop */}
                    <Box sx={{ marginBottom: '2.5rem' }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: '18.125rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#ffffff',
                                borderRadius: '1.125rem',
                                border: dragActive ? '0.125rem dashed #29AEF8' : '0.125rem dashed #E0E0E0',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: '#29AEF8',
                                    backgroundColor: 'rgba(41, 174, 248, 0.02)'
                                }
                            }}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            component="label"
                        >
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls,.ods,.txt,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/plain,application/octet-stream"
                                onChange={handleFileInput}
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                            />
                            <Box sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: '5rem',
                                        height: '5rem',
                                        backgroundColor: '#F7F7F7',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1.5rem'
                                    }}
                                >
                                    <CSVFileIcon sx={{ fontSize: '2.5rem', color: '#4F4F4F' }} />
                                </Box>
                                <Typography variant="h6" sx={{ mb: '0.0625rem', color: '#000000', fontSize: '1.5rem', fontWeight: 600 }}>
                                    <span style={{ color: '#29AEF8' }}>Select CSV or Excel file</span> to upload
                                </Typography>
                                <Typography sx={{ color: '#4F4F4F', fontSize: '1rem', fontWeight: 500 }}>
                                    or drag and drop it here
                                </Typography>
                                <Typography sx={{ color: '#828282', fontSize: '0.8125rem', mt: '1.875rem' }}>
                                    Must be <b>.csv, .xlsx, .xls</b> file with maximum file size <b>50MB</b>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* How to format the CSV */}
                    <Box sx={{ mb: '2.5rem' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '2.5rem' }}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: '0.0625rem', fontSize: '1rem', fontWeight: 600, color: '#000000' }}>How to format the CSV</Typography>
                                <Typography sx={{ color: '#4F4F4F', fontSize: '0.8125rem' }}>
                                    Take a look at the possible columns below or download the CSV template.
                                </Typography>
                            </Box>
                            <Box
                                onClick={downloadTemplate}
                                sx={{
                                    backgroundColor: '#FFFFFF',
                                    color: '#4F4F4F',
                                    border: '0.0625rem solid #E0E0E0',
                                    padding: '0 1.5rem',
                                    height: '2.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    borderRadius: '6.25rem',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: '#FFFFFF'
                                    }
                                }}
                            >
                                <FileDownloadOutlinedIcon sx={{ fontSize: '1rem' }} />
                                Download CSV Template
                            </Box>
                        </Box>
                        <TeamMemberRequiredFields />
                    </Box>

                    {/* Error Display */}
                    {error && (
                        <Alert
                            severity={validationResults.invalid > 0 ? "warning" : "error"}
                            sx={{ marginBottom: '1rem' }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Validation Summary */}
                    {csvData.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                                <Chip
                                    icon={<CheckCircleOutlineIcon />}
                                    label={`${validationResults.valid} Valid`}
                                    color="success"
                                    variant="outlined"
                                />
                                {validationResults.invalid > 0 && (
                                    <Chip
                                        label={`${validationResults.invalid} Invalid`}
                                        color="error"
                                        variant="outlined"
                                    />
                                )}
                            </Box>

                            {/* Data Preview */}
                            <TableContainer sx={{ maxHeight: '18.75rem', border: '0.0625rem solid #e0e0e0', borderRadius: '0.5rem' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Job Title</TableCell>
                                            <TableCell>Company</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Phone</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvData.slice(0, 10).map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={row.isValid ? "Valid" : "Invalid"}
                                                        color={row.isValid ? "success" : "error"}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.name || '-'}</TableCell>
                                                <TableCell>{row.jobtitle || '-'}</TableCell>
                                                <TableCell>{row.company || '-'}</TableCell>
                                                <TableCell>{row.location || '-'}</TableCell>
                                                <TableCell>{row.phonenumber || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {csvData.length > 10 && (
                                <Typography variant="caption" sx={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                                    Showing first 10 rows of {csvData.length} total rows
                                </Typography>
                            )}
                        </Box>
                    )}
                </>
            )}

            {/* Review Step */}
            {currentStep === 2 && (
                <Box sx={{ width: '100%' }}>
                    <Box>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1.25rem' }}>
                            Choose columns to be imported
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 4, color: '#828282' }}>
                            This is a preview of your CSV file and we have found {csvData.length > 0 ? Object.keys(csvData[0]).length : 0} columns to use. Select which columns to include below.
                        </Typography>

                        <Table sx={{
                            minWidth: 900,
                            '& .MuiTableCell-root': {
                                padding: '8px 20px'
                            }
                        }}>
                            <TableHead>
                                <TableRow sx={{
                                    '& th': {
                                        fontWeight: 'bold',
                                        backgroundColor: '#f9f9f9'
                                    }
                                }}>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px', minWidth: '200px' }}>Select Touch Field</TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px', minWidth: '150px' }}>Customize Field</TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px', minWidth: '150px' }}>Update If Exists</TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px', minWidth: '200px' }}>Select CSV Column</TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px', minWidth: '150px' }}>Sample Data</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Account Email (Required) */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.email}
                                                disabled={true}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Account Email (Required)
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.email || ''}
                                                onChange={(e) => handleMappingChange('email', e.target.value)}
                                                displayEmpty
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('email').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Name */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.name}
                                                onChange={() => handleCheckboxChange('name')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Name
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.name || ''}
                                                onChange={(e) => handleMappingChange('name', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.name}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('name').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Job Title */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.jobtitle}
                                                onChange={() => handleCheckboxChange('jobtitle')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Job Title
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.jobtitle || ''}
                                                onChange={(e) => handleMappingChange('jobtitle', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.jobtitle}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('jobtitle').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Company */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.company}
                                                onChange={() => handleCheckboxChange('company')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Company
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.company || ''}
                                                onChange={(e) => handleMappingChange('company', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.company}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('company').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Location */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.location}
                                                onChange={() => handleCheckboxChange('location')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Location
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.location || ''}
                                                onChange={(e) => handleMappingChange('location', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.location}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('location').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Bio */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.bio}
                                                onChange={() => handleCheckboxChange('bio')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Bio
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.bio || ''}
                                                onChange={(e) => handleMappingChange('bio', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.bio}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('bio').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Photo */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.photo}
                                                onChange={() => handleCheckboxChange('photo')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Photo
                                            </Typography>
                                            <InfoOutlinedIcon sx={{
                                                fontSize: '1rem',
                                                marginLeft: 0.5,
                                                color: '#828282'
                                            }} />
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Customize Field - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.photo || ''}
                                                onChange={(e) => handleMappingChange('photo', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.photo}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('photo').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Links & Contact Info Heading */}
                                <TableRow>
                                    <TableCell colSpan={5} sx={{
                                        border: 'none',
                                        padding: '20px 20px 12px 20px',
                                        backgroundColor: '#f9f9f9'
                                    }}>
                                        <Typography variant="h6" sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            color: '#000000',
                                            margin: 0
                                        }}>
                                            Links & Contact Info
                                        </Typography>
                                    </TableCell>
                                </TableRow>

                                {/* Email */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.email2}
                                                onChange={() => handleCheckboxChange('email2')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Email
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {checkedFields.email2 && (
                                            <TeamContactLinkItem
                                                type="email"
                                                onClick={() => handleContactLinkClick('email2')}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.email2 || ''}
                                                onChange={(e) => handleMappingChange('email2', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.email2}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('email2').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Phone Number */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.phonenumber}
                                                onChange={() => handleCheckboxChange('phonenumber')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Phone Number
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {checkedFields.phonenumber && (
                                            <TeamContactLinkItem
                                                type="phone"
                                                onClick={() => handleContactLinkClick('phonenumber')}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.phonenumber || ''}
                                                onChange={(e) => handleMappingChange('phonenumber', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.phonenumber}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('phonenumber').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* Website */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.website}
                                                onChange={() => handleCheckboxChange('website')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                Website
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {checkedFields.website && (
                                            <TeamContactLinkItem
                                                type="website"
                                                onClick={() => handleContactLinkClick('website')}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.website || ''}
                                                onChange={(e) => handleMappingChange('website', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.website}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('website').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>

                                {/* LinkedIn */}
                                <TableRow>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Checkbox
                                                checked={checkedFields.linkedin}
                                                onChange={() => handleCheckboxChange('linkedin')}
                                                sx={{
                                                    color: '#e0e0e0',
                                                    '&.Mui-checked': {
                                                        color: '#000',
                                                    },
                                                }}
                                            />
                                            <Typography variant="body2">
                                                LinkedIn
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {checkedFields.linkedin && (
                                            <TeamContactLinkItem
                                                type="linkedin"
                                                onClick={() => handleContactLinkClick('linkedin')}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Update If Exists - empty for now */}
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        <FormControl sx={{ minWidth: 250 }}>
                                            <Select
                                                value={mappings.linkedin || ''}
                                                onChange={(e) => handleMappingChange('linkedin', e.target.value)}
                                                displayEmpty
                                                disabled={!checkedFields.linkedin}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    borderRadius: '12px',
                                                    '& .MuiSelect-select': {
                                                        borderRadius: '12px',
                                                        padding: '6px 8px',
                                                        paddingRight: '32px !important',
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '8px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Unmapped</em>
                                                </MenuItem>
                                                {getAvailableColumns('linkedin').map((column) => (
                                                    <MenuItem key={column} value={column}>
                                                        {column}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                        {/* Sample Data - empty for now */}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setCurrentStep(1)}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: '1px solid #e3e3e3',
                                    textTransform: 'none',
                                    padding: '0.5rem 2rem',
                                    minWidth: '180px',
                                    height: '40px'
                                }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={async () => {
                                    await handleImportMembers();
                                }}
                                disabled={!mappings.email || isCreatingMembers || csvData.length === 0}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    borderRadius: '1.25rem',
                                    textTransform: 'none',
                                    padding: '0.5rem 2rem',
                                    minWidth: '180px',
                                    height: '40px',
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                            >
                                {isCreatingMembers ? 'Importing...' : 'Import Members'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Contact Link Configuration Modal - Removed as component was deleted */}
            {/* TODO: Implement contact link configuration for CSV import if needed */}
            
            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />
        </Box>
    );
};

export default TeamImportCsvContent;
